import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, ErrorBox, LoadingRows } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtDateTime, titleCase } from "@/lib/format";
import { RotateCcw, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["account", "opportunity", "contact", "task", "icp", "offer", "template", "suppression"];

export default function TrashPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);

  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?entity_type=${filter}`;
    return api.get(`/trash${q}`).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errMsg(e)));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const restore = async (entry) => {
    setBusy(entry.id);
    try { await api.post(`/trash/${entry.id}/restore`); toast.success(`Restored ${entry.label || entry.entity_type}`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const purge = async () => {
    const entry = purgeTarget; setBusy(entry.id);
    try { await api.post(`/trash/${entry.id}/purge`, { confirm: true }); toast.success(`Permanently deleted ${entry.label || entry.entity_type}`); setPurgeTarget(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  const items = data?.items || [];
  const canPurge = !!data?.can_purge;

  return (
    <div className="page">
      <PageHeader title="Trash" subtitle="Deleted items are kept here so nothing is lost by accident. Restore them, or (platform admin) remove them permanently. Items blocked by dependencies were never deleted."
        actions={
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="trash-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t} value={t}>{titleCase(t)}</SelectItem>)}
            </SelectContent>
          </Select>
        } />

      {error && <ErrorBox error={error} onRetry={load} />}

      {!data ? <div className="table-wrap"><LoadingRows /></div> : items.length === 0 ? (
        <div className="panel p-10 text-center" data-testid="trash-empty">
          <Trash2 className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-heading font-semibold">The Trash is empty</div>
          <p className="meta mt-1">Deleted accounts, opportunities, contacts, tasks and more will appear here, ready to restore.</p>
        </div>
      ) : (
        <div className="table-wrap overflow-x-auto"><table className="w-full table-dense" data-testid="trash-table">
          <thead><tr><th>Type</th><th>Item</th><th className="text-right">Also removed</th><th>Removed by</th><th>When</th><th>Reason</th><th></th></tr></thead>
          <tbody>{items.map((e) => (
            <tr key={e.id} className="cursor-default" data-testid={`trash-row-${e.id}`}>
              <td><span className="chip chip-muted">{titleCase(e.entity_type)}</span></td>
              <td><div className="font-medium truncate max-w-[280px]">{e.label || <span className="meta">(unnamed)</span>}</div></td>
              <td className="text-right nums">{e.dependents > 0 ? e.dependents : <span className="meta">—</span>}</td>
              <td className="text-xs">{e.trashed_by_name || <span className="meta">—</span>}</td>
              <td className="meta whitespace-nowrap">{fmtDateTime(e.trashed_at)}</td>
              <td className="text-xs text-muted-foreground truncate max-w-[200px]">{e.reason || "—"}</td>
              <td className="text-right whitespace-nowrap">
                <Button size="sm" variant="outline" disabled={busy === e.id} onClick={() => restore(e)} data-testid={`trash-restore-${e.id}`}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" />Restore
                </Button>{" "}
                {canPurge && (
                  <Button size="sm" variant="ghost" className="text-[hsl(var(--danger))]" disabled={busy === e.id} onClick={() => setPurgeTarget(e)} data-testid={`trash-purge-${e.id}`}>
                    Delete forever
                  </Button>
                )}
              </td>
            </tr>))}</tbody></table></div>
      )}

      <AlertDialog open={!!purgeTarget} onOpenChange={(v) => !v && setPurgeTarget(null)}>
        <AlertDialogContent data-testid="trash-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-[hsl(var(--danger))]" />Permanently delete {purgeTarget?.label || purgeTarget?.entity_type}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the item and its {purgeTarget?.dependents > 0 ? `${purgeTarget.dependents} dependent record(s)` : "record"} for good. It cannot be undone and cannot be restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={purge} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger)/0.9)]" data-testid="trash-purge-confirm">Delete forever</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
